import { useAuth } from "@/context/auth-context";
import { AUTH_CONFIG } from "@/constants";
import {
  USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS,
  useUserOnboardingQuestion,
} from "@/queries/onboarding";
import { useCurrentUser } from "@/queries/users";
import { useWorkspaces } from "@/queries";

/**
 * Related to the release of the v2 onboarding flow.
 */
const OLD_USER_THRESHOLD_DATE = new Date("2026-03-22");

export const useOnboardingStatus = () => {
  const auth = useAuth();
  const userToken = auth?.userToken;
  const { isOnPrem } = AUTH_CONFIG;

  const { data: onboardingAnswers, isLoading: isLoadingOnboarding } = useUserOnboardingQuestion(
    userToken ?? "",
    { enabled: !isOnPrem },
  );
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();
  const { data: workspacesData, isLoading: isLoadingWorkspaces } = useWorkspaces();

  const isLoading =
    (isOnPrem ? false : isLoadingOnboarding) || isLoadingUser || isLoadingWorkspaces;

  const userData = currentUser || auth?.user;

  const onboardingResponses = onboardingAnswers?.onboarding_responses ?? [];
  const completedKeys = new Set(
    onboardingResponses.map(response => response.onboarding_question_key),
  );

  const hasAboutUser = completedKeys.has(USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.ABOUT_USER);
  const hasUseCase = completedKeys.has(USER_ONBOARDING_REQUEST_PARAM_QUESTION_KEYS.USE_CASE);

  const hasOrganizations =
    userData?.memberships?.organizations && userData.memberships.organizations.length > 0;
  const hasWorkspaces = Boolean(workspacesData?.workspaces?.length);

  const isInvitee = hasOrganizations && !hasAboutUser;
  const isOldUser =
    hasOrganizations &&
    userData?.created_at &&
    new Date(userData.created_at) < OLD_USER_THRESHOLD_DATE;

  const isOnboardingComplete = isOnPrem
    ? Boolean(isOldUser || (hasOrganizations && hasWorkspaces))
    : Boolean(isInvitee || isOldUser || (hasAboutUser && hasOrganizations && hasUseCase));
  const shouldShowProductSetup =
    !isOnPrem && hasAboutUser && hasOrganizations && hasWorkspaces && !hasUseCase;

  return {
    isLoading,
    hasAboutUser,
    hasOrganizations,
    hasWorkspaces,
    hasUseCase,
    isInvitee,
    isOldUser,
    isOnPrem,
    isOnboardingComplete,
    shouldShowProductSetup,
  };
};
